/**
 * @file
 * Decision Tree Behavior - Uses standard anchor links for editor simplicity
 *
 * Editors create content with:
 * - Standard <details id="section-name"> elements
 * - Normal anchor links <a href="#next-section"> so nuthin special for CKEditor to make this work
 * - No special attributes or classes needed except wrapper div for code to act on
 *
 * URL result: /page#start#q3#q4 (multiple hashes showing the path)
 *
 * For Example: (what editors create in CKEditor):
 *
 * <div class="decision-tree">
 *   <details id="start">
 *     <summary>Question 1</summary>
 *     <div>
 *       <p>Do you need approval?</p>
 *       <p><a href="#q2">Yes</a> <a href="#q3">No</a></p>
 *     </div>
 *   </details>
 *
 *   <details id="q2">...</details>
 *   <details id="q3">...</details>
 * </div>
 */

(function (Drupal, once) {
  'use strict';

  Drupal.behaviors.decisionTree = {

    config: {
      containerSelector: '.decision-tree', // Wrapper class that identifies a decision tree
      sectionSelector: 'details[id]', // Sections are details elements with IDs
      scrollOffset: 100, // Scroll offset from viewport top
      scrollBehavior: 'smooth', // Change scroll method if the business area doesn't like "smooth"
    }, attach: function (context, settings) {
      const self = this;

      once('decision-tree', this.config.containerSelector, context).forEach(function (container) {
        self.init(container);
      });
    },

    init: function (container) {
      const self = this;
      this.container = container;
      // Build sections map
      this.sections = {};
      container.querySelectorAll(this.config.sectionSelector).forEach(function (section) {
        if (section.id) {
          self.sections[section.id] = section;
        }
      });

      // Current path of visible sections
      this.path = [];

      // Find start section or one with class 'decision-start'
      const startSection = container.querySelector('.decision-start') || container.querySelector(this.config.sectionSelector);
      this.startId = startSection ? startSection.id : null;

      // Intercept clicks on hash links within container
      container.addEventListener('click', function (e) {
        const link = e.target.closest('a[href^="#"]');
        if (link) {
          const targetId = link.getAttribute('href').slice(1);
          // Only intercept if target is one of our sections, there may be other anchor links on the page we don't care about. This lets Editors place hyperlinks inside the <details> without affecting the decision-tree or the hyperlinks within.
          if (self.sections[targetId]) {
            e.preventDefault();
            self.handleNavigation(targetId, link);
          }
        }
      });

      // Prevent native details toggle since we control open state via previous decisions
      container.querySelectorAll(this.config.sectionSelector).forEach(function (details) {
        details.querySelector('summary').addEventListener('click', function (e) {
          // Only prevent if this section is in path. let hidden ones be clickable if somehow visible due to browser changes, preferences, accessibility settings or plugins.
          if (self.path.includes(details.id)) {
            e.preventDefault();
          }
        });
      });

      // Hide all sections to start with
      this.hideAll();

      // Restore from URL or show 'decision-start', or first <details>
      const hashPath = this.parseUrl();
      if (hashPath.length > 0) {
        this.restorePath(hashPath, true);
      } else if (this.startId) {
        this.showSection(this.startId, false);
      }

      // Handle browser back/forward with state
      window.addEventListener('popstate', function () {
        const path = self.parseUrl();
        if (path.length > 0) {
          self.restorePath(path, false);
        } else if (self.startId) {
          self.path = [];
          self.hideAll();
          self.showSection(self.startId, false);
        }
      });
    },

    // User navigation to a section then scroll down to top of that new section
    handleNavigation: function (targetId, clickedLink) {
      const currentSection = clickedLink.closest(this.config.sectionSelector);
      const currentId = currentSection ? currentSection.id : null;
      const currentIndex = this.path.indexOf(currentId);
      const targetIndex = this.path.indexOf(targetId);

      if (targetIndex !== -1) {
        // Clicking a section already in path - truncate to that point
        this.path = this.path.slice(0, targetIndex + 1);
      } else if (currentIndex !== -1) {
        // Navigating forward from a section in path, truncate anything after current, then add target
        this.path = this.path.slice(0, currentIndex + 1);
        this.path.push(targetId);
      } else {
        // Navigating from outside the path
        this.path.push(targetId);
      }

      this.render();
      this.updateUrl();
      this.scrollTo(targetId);
    },

    // Show a single section
    showSection: function (sectionId, scroll) {
      this.path = [sectionId];
      this.render();
      this.updateUrl();
      if (scroll) {
        this.scrollTo(sectionId);
      }
    },

    // Restore the full path
    restorePath: function (pathArray, scroll) {
      const self = this;
      // Filter to only valid section IDs, let browser handle all other IDs normally
      this.path = pathArray.filter(function (id) {
        return self.sections[id];
      });

      if (this.path.length === 0 && this.startId) {
        this.path = [this.startId];
      }

      this.render();

      if (scroll && this.path.length > 0) {
        this.scrollTo(this.path[this.path.length - 1]);
      }
    },

    // Hide all sections
    hideAll: function () {
      Object.values(this.sections).forEach(function (section) {
        section.style.display = 'none';
        section.removeAttribute('open');
      });
    },

    // Render current path state.
    render: function () {
      const self = this;
      this.hideAll();

      // Clear all selected link states
      this.container.querySelectorAll('a.decision-selected').forEach(function (link) {
        link.classList.remove('decision-selected');
      });

      // Show sections in path
      this.path.forEach(function (sectionId, index) {
        const section = self.sections[sectionId];
        if (section) {
          section.style.display = '';
          section.setAttribute('open', 'open');

          // Mark the last section as active
          section.classList.toggle('decision-active', index === self.path.length - 1);

          // Mark the link that leads to next section in path
          if (index < self.path.length - 1) {
            const nextId = self.path[index + 1];
            const link = section.querySelector('a[href="#' + nextId + '"]');
            if (link) {
              link.classList.add('decision-selected');
            }
          }
        }
      });
    },

    // Update URL to reflect current path. Format: `/page#start#q3#q4`
    updateUrl: function () {
      const hash = this.path.map(function (id) {
        return '#' + id;
      }).join('');

      const newUrl = window.location.pathname + window.location.search + hash;
      history.pushState({path: this.path.slice()}, '', newUrl);
    },

    // Parse URL hash tags into path array we can use with loops
    parseUrl: function () {
      const hash = window.location.hash;
      if (!hash || hash.length < 2) {
        return [];
      }

      // Split on # and filter empties (yes, testers will do this)
      return hash.split('#').filter(function (s) {
        return s.length > 0;
      });
    },

    // Scroll a section into view
    scrollTo: function (sectionId) {
      const self = this;
      const section = this.sections[sectionId];
      if (!section) {
        return;
      }

      setTimeout(function () {
        const rect = section.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const target = scrollTop + rect.top - self.config.scrollOffset;

        window.scrollTo({
          top: Math.max(0, target), behavior: self.config.scrollBehavior,
        });

        // Focus the summary for screen readers so that the title/summary is read first
        const summary = section.querySelector('summary');
        if (summary) {
          summary.setAttribute('tabindex', '-1');
          summary.focus({preventScroll: true});
        }
      }, 50);
    },
  };

})(Drupal, once);
